/**
 * Connecting a plugin account over OAuth.
 *
 * The backend starts a flow and returns the provider's consent `url` plus a `browserProof`. The provider
 * then sends the browser back to one fixed page, `/oauth/callback?state=…&code=…`, and the frontend
 * finishes the flow by sending `state`, `code` and `browserProof` to `complete`.
 *
 * Popup flow, the default:
 *   1. `connectOAuth` opens a popup inside the click, starts the flow and points the popup at the provider.
 *      The `browserProof` stays in memory in this tab.
 *   2. The callback page in the popup posts the result on a BroadcastChannel. `window.opener` cannot be
 *      used for this: provider consent pages send Cross-Origin-Opener-Policy, which severs it.
 *   3. The flow whose `state` matches acknowledges the result and calls `complete`. When nothing
 *      acknowledges it (the flow was cancelled or expired), the callback page tells the user to start again.
 *
 * Same-tab flow, when the popup is blocked (the usual case on mobile):
 *   `browserProof` and the current page are saved to sessionStorage, the tab goes to the provider, and the
 *   callback page calls `complete` itself and returns to the saved page.
 */

export interface OAuthStart {
	url: string;
	browserProof: string;
}

export interface OAuthCompleteInput {
	state: string;
	code: string;
	browserProof: string;
}

/** What the provider appended to the callback URL: `state` and `code`, or an `error`. */
export interface OAuthCallbackResult {
	state: string | null;
	code: string | null;
	error: string | null;
	errorDescription: string | null;
}

export interface OAuthPendingRedirect {
	browserProof: string;
	/** Path and query of the page that started the flow. */
	returnTo: string;
}

export type OAuthFlowErrorReason = "cancelled" | "expired" | "provider";

export class OAuthFlowError extends Error {
	reason: OAuthFlowErrorReason;

	constructor(reason: OAuthFlowErrorReason, message: string) {
		super(message);
		this.reason = reason;
	}
}

type ChannelMessage = { type: "result"; result: OAuthCallbackResult } | { type: "ack"; state: string };

const CHANNEL_NAME = "oauth";
const PENDING_REDIRECT_KEY = "oauth:pending-redirect";
/** The backend forgets a started flow after 10 minutes; `complete` cannot succeed after that. */
const FLOW_LIFETIME_MS = 10 * 60 * 1000;
/** How long the callback page waits for a tab to claim the result. */
const ACK_TIMEOUT_MS = 3000;

// --- The tab that starts the flow ---

/**
 * Call it synchronously from the click handler, not after an await: the popup is opened before the first
 * await, and a popup opened later is no longer tied to the click, so the browser blocks it.
 *
 * Resolves "connected" once `complete` succeeded, or "redirected" when this tab is leaving for the provider.
 * Rejects with an `OAuthFlowError` when the flow is cancelled, expires or the provider returns an error,
 * and with whatever `start` or `complete` threw otherwise.
 */
export async function connectOAuth({
	start,
	complete,
	signal,
	lifetimeMs = FLOW_LIFETIME_MS,
}: {
	start: () => Promise<OAuthStart>;
	complete: (input: OAuthCompleteInput) => Promise<unknown>;
	/** Aborting it cancels the wait for the popup. */
	signal?: AbortSignal;
	lifetimeMs?: number;
}): Promise<"connected" | "redirected"> {
	if (typeof BroadcastChannel === "undefined") {
		redirectToProvider(await start());
		return "redirected";
	}

	// The popup receives a copy of this tab's sessionStorage. A marker left over from an abandoned
	// same-tab flow would make the popup's callback page complete the flow on its own.
	clearPendingRedirect();
	const popup = window.open("about:blank", "_blank", "popup,width=520,height=720");
	const isPopupBlocked = !popup || popup.closed;

	let flow: OAuthStart;
	try {
		flow = await start();
	} catch (error) {
		popup?.close();
		throw error;
	}

	if (isPopupBlocked) {
		redirectToProvider(flow);
		return "redirected";
	}
	if (popup.closed || signal?.aborted) {
		popup.close();
		throw new OAuthFlowError("cancelled", "The connection window was closed");
	}

	const result = waitForResult(popup, flow, signal, lifetimeMs);
	popup.location.href = flow.url;
	await complete(completeInput(await result, flow.browserProof));
	return "connected";
}

/**
 * Ends on the result, on `signal` aborting, or when the flow expires.
 *
 * `popup.closed` is deliberately not polled: once the provider's page loads, Cross-Origin-Opener-Policy
 * severs our handle and `closed` reads true while the window is still open. A user who closes the popup
 * cancels through `signal` instead.
 */
function waitForResult(
	popup: Window,
	flow: OAuthStart,
	signal: AbortSignal | undefined,
	lifetimeMs: number
): Promise<OAuthCallbackResult> {
	const state = new URL(flow.url).searchParams.get("state");
	const channel = new BroadcastChannel(CHANNEL_NAME);
	let expiry: ReturnType<typeof setTimeout> | undefined;
	let cancel: (() => void) | undefined;

	return new Promise<OAuthCallbackResult>((resolve, reject) => {
		channel.addEventListener("message", (event: MessageEvent<ChannelMessage>) => {
			const message = event.data;
			// Other flows (another tab, an earlier click) share the channel; `state` tells whose result it is.
			if (message.type !== "result" || !state || message.result.state !== state) return;

			channel.postMessage({ type: "ack", state } satisfies ChannelMessage);
			resolve(message.result);
		});

		expiry = setTimeout(() => {
			reject(new OAuthFlowError("expired", "The connection request expired, try again"));
		}, lifetimeMs);

		cancel = () => reject(new OAuthFlowError("cancelled", "Connecting was cancelled"));
		signal?.addEventListener("abort", cancel);
	}).finally(() => {
		channel.close();
		clearTimeout(expiry);
		if (cancel) signal?.removeEventListener("abort", cancel);
		// Does nothing once the handle is severed, but closes the popup while it is still on our origin.
		popup.close();
	});
}

function redirectToProvider(flow: OAuthStart): void {
	const pending: OAuthPendingRedirect = {
		browserProof: flow.browserProof,
		returnTo: location.pathname + location.search,
	};
	sessionStorage.setItem(PENDING_REDIRECT_KEY, JSON.stringify(pending));
	location.assign(flow.url);
}

// --- The callback page ---

export function parseCallback(search: string): OAuthCallbackResult {
	const params = new URLSearchParams(search);
	return {
		state: params.get("state"),
		code: params.get("code"),
		error: params.get("error"),
		errorDescription: params.get("error_description"),
	};
}

/** Throws the provider's error when the callback carries one instead of a code. */
export function completeInput(result: OAuthCallbackResult, browserProof: string): OAuthCompleteInput {
	if (result.error || !result.state || !result.code) {
		const fallback =
			result.error === "access_denied" ? "Access was denied" : "The provider did not return an authorization code";
		throw new OAuthFlowError("provider", result.errorDescription || fallback);
	}
	return { state: result.state, code: result.code, browserProof };
}

/** Present only in the tab that went to the provider itself, because its popup was blocked. */
export function readPendingRedirect(): OAuthPendingRedirect | null {
	const saved = sessionStorage.getItem(PENDING_REDIRECT_KEY);
	return saved ? (JSON.parse(saved) as OAuthPendingRedirect) : null;
}

export function clearPendingRedirect(): void {
	sessionStorage.removeItem(PENDING_REDIRECT_KEY);
}

/** Popup flow: hands the result to the tab that started the flow. Resolves false when no tab claims it. */
export function relayResult(result: OAuthCallbackResult, ackTimeoutMs = ACK_TIMEOUT_MS): Promise<boolean> {
	const channel = new BroadcastChannel(CHANNEL_NAME);
	let giveUp: ReturnType<typeof setTimeout> | undefined;

	return new Promise<boolean>((resolve) => {
		channel.addEventListener("message", (event: MessageEvent<ChannelMessage>) => {
			const message = event.data;
			if (message.type === "ack" && message.state === result.state) resolve(true);
		});
		giveUp = setTimeout(() => resolve(false), ackTimeoutMs);
		channel.postMessage({ type: "result", result } satisfies ChannelMessage);
	}).finally(() => {
		channel.close();
		clearTimeout(giveUp);
	});
}
